import { Bell, Menu, Sparkles, Compass, BookOpen } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppUpdate } from "@/hooks/useAppUpdate";
import logoDC from "@/assets/logo-dc.png";

interface MobileHeaderProps {
  credits?: number;
  loading?: boolean;
  onMenuClick?: () => void;
  onNotificationClick?: () => void;
  onExploreClick?: () => void;
  onBlogClick?: () => void;
  unreadCount?: number;
}

export const MobileHeader = ({
  credits = 0,
  loading = false,
  onMenuClick,
  onNotificationClick,
  onExploreClick,
  onBlogClick,
  unreadCount = 0,
}: MobileHeaderProps) => {
  const { updateAvailable } = useAppUpdate();
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border safe-area-inset-top">
      <div className="flex items-center justify-between px-4 py-3">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <img
            src={logoDC}
            alt="Decor Canvas"
            className="h-8 w-auto object-cover"
            style={{ transform: 'scale(1.6)' }}
          />
          <div className="flex flex-col leading-tight">
            <span className="text-md md:text-md font-bold text-black whitespace-pre-line">
  {"Decor\nCanvas"}
</span>
            <span className="text-[10px] sm:text-xs text-muted-foreground">
              {/* Where AI meets aesthetics */}
            </span>
          </div>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-0.5">
          {/* Notifications */}
          <button
            onClick={onNotificationClick}
            className="relative p-1.5 rounded-full hover:bg-muted transition-colors"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5 text-foreground" />
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 h-2 w-2 bg-destructive rounded-full" />
            )}
          </button>

          {/* Explore */}
          <button
            onClick={onExploreClick}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
            aria-label="Explore"
          >
            <Compass className="h-5 w-5 text-foreground" />
          </button>

          {/* Blog */}
          <button
            onClick={onBlogClick}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
            aria-label="Blog"
          >
            <BookOpen className="h-5 w-5 text-foreground" />
          </button>

          {/* Credits Badge */}
          {loading ? (
            <Skeleton className="h-8 w-24 rounded-full" />
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 rounded-full">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-xs sm:text-sm font-medium text-primary whitespace-nowrap">
                {credits} Credits
              </span>
            </div>
          )}

          {/* Menu */}
          <button
            onClick={onMenuClick}
            className="relative p-1.5 rounded-full hover:bg-muted transition-colors"
            aria-label="Menu"
          >
            <Menu className="h-5 w-5 text-foreground" />
            {updateAvailable && (
              <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-[hsl(15,65%,55%)] ring-2 ring-white" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
};
